import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { I } from '../icons.jsx';
import { TextField, Footer, VividWordmark, FormAlert } from '../primitives.jsx';
import { Banner, Button, Spinner } from '../components/v24/index.jsx';
import { api, errorMessage } from '../api.js';
import { storage } from '../storage.js';
import { handoffRedirect } from '../sso.js';
// AUTH-554 added this page's language and country controls and the localeOptions
// module they read, but never the import between them: LANGUAGES,
// DOCUMENT_LANGUAGES, LANGUAGE_NAMES and localisedCountries were all referenced
// and none was in scope, so the page threw `ReferenceError: LANGUAGES is not
// defined` on render -- the onboarding wizard, on the signup path. Nothing
// caught it because that PR's tests are backend-only and no spec rendered this
// page; the three specs added here are the first that do.
import {
  DOCUMENT_LANGUAGES,
  LANGUAGES,
  LANGUAGE_NAMES,
  localisedCountries,
} from '../localeOptions.js';

// Four-step onboarding wizard. There is no Security step (AUTH-504): the
// platform has no second factor yet (AUTH-160), so a step asking the owner
// to enable one could not be completed, and its "remind me later" wrote a
// key to this origin's localStorage that the ERP shell, on another origin,
// can never read. The Review step states the position instead. When
// ENG-160 ships, reinstate a step that can be completed (enrolment, a
// verified first code, recovery codes) and keep any reminder on the user
// record returned by /auth/me, not in browser storage.
const STEPS = [
  { id: 1, label: 'Company' },
  { id: 2, label: 'Region' },
  { id: 3, label: 'Plan' },
  { id: 4, label: 'Review' },
];

// The industry list is NOT kept here: it is the bundle catalogue
// (GET /auth/onboarding/bundles, Grid's industry_bundle), joined on the
// bundle slug. A name is a label and can be renamed in Grid (AUTH-659).
// Value of the "Other" choice in the bundle-driven industry select.
const OTHER = '__other__';
const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];
const SIZES = ['1–10', '11–50', '51–200', '201–500', '500+'];
// Comprehensive ISO 3166-1 alpha-2 country list with the canonical
// ISO 4217 currency and a representative IANA timezone for the
// commercial hub of each jurisdiction. Sorted alphabetically by display
// name so the dropdown reads naturally. Picking a country on the
// Region step mirrors these fields into ``state.currency`` and
// ``state.timezone`` automatically. The backend then uses the country
// code to attach the right country pack (chart of accounts, tax rules,
// payslip format) to the new tenant.
const COUNTRIES = [
  { code: 'AF', name: 'Afghanistan',                       currency: 'AFN', timezone: 'Asia/Kabul' },
  { code: 'AL', name: 'Albania',                           currency: 'ALL', timezone: 'Europe/Tirane' },
  { code: 'DZ', name: 'Algeria',                           currency: 'DZD', timezone: 'Africa/Algiers' },
  { code: 'AR', name: 'Argentina',                         currency: 'ARS', timezone: 'America/Argentina/Buenos_Aires' },
  { code: 'AM', name: 'Armenia',                           currency: 'AMD', timezone: 'Asia/Yerevan' },
  { code: 'AU', name: 'Australia',                         currency: 'AUD', timezone: 'Australia/Sydney' },
  { code: 'AT', name: 'Austria',                           currency: 'EUR', timezone: 'Europe/Vienna' },
  { code: 'AZ', name: 'Azerbaijan',                        currency: 'AZN', timezone: 'Asia/Baku' },
  { code: 'BH', name: 'Bahrain',                           currency: 'BHD', timezone: 'Asia/Bahrain' },
  { code: 'BD', name: 'Bangladesh',                        currency: 'BDT', timezone: 'Asia/Dhaka' },
  { code: 'BY', name: 'Belarus',                           currency: 'BYN', timezone: 'Europe/Minsk' },
  { code: 'BE', name: 'Belgium',                           currency: 'EUR', timezone: 'Europe/Brussels' },
  { code: 'BO', name: 'Bolivia',                           currency: 'BOB', timezone: 'America/La_Paz' },
  { code: 'BA', name: 'Bosnia and Herzegovina',            currency: 'BAM', timezone: 'Europe/Sarajevo' },
  { code: 'BR', name: 'Brazil',                            currency: 'BRL', timezone: 'America/Sao_Paulo' },
  { code: 'BN', name: 'Brunei',                            currency: 'BND', timezone: 'Asia/Brunei' },
  { code: 'BG', name: 'Bulgaria',                          currency: 'BGN', timezone: 'Europe/Sofia' },
  { code: 'KH', name: 'Cambodia',                          currency: 'KHR', timezone: 'Asia/Phnom_Penh' },
  { code: 'CM', name: 'Cameroon',                          currency: 'XAF', timezone: 'Africa/Douala' },
  { code: 'CA', name: 'Canada',                            currency: 'CAD', timezone: 'America/Toronto' },
  { code: 'CL', name: 'Chile',                             currency: 'CLP', timezone: 'America/Santiago' },
  { code: 'CN', name: 'China',                             currency: 'CNY', timezone: 'Asia/Shanghai' },
  { code: 'CO', name: 'Colombia',                          currency: 'COP', timezone: 'America/Bogota' },
  { code: 'CR', name: 'Costa Rica',                        currency: 'CRC', timezone: 'America/Costa_Rica' },
  { code: 'HR', name: 'Croatia',                           currency: 'EUR', timezone: 'Europe/Zagreb' },
  { code: 'CY', name: 'Cyprus',                            currency: 'EUR', timezone: 'Asia/Nicosia' },
  { code: 'CZ', name: 'Czechia',                           currency: 'CZK', timezone: 'Europe/Prague' },
  { code: 'DK', name: 'Denmark',                           currency: 'DKK', timezone: 'Europe/Copenhagen' },
  { code: 'DO', name: 'Dominican Republic',                currency: 'DOP', timezone: 'America/Santo_Domingo' },
  { code: 'EC', name: 'Ecuador',                           currency: 'USD', timezone: 'America/Guayaquil' },
  { code: 'EG', name: 'Egypt',                             currency: 'EGP', timezone: 'Africa/Cairo' },
  { code: 'SV', name: 'El Salvador',                       currency: 'USD', timezone: 'America/El_Salvador' },
  { code: 'EE', name: 'Estonia',                           currency: 'EUR', timezone: 'Europe/Tallinn' },
  { code: 'ET', name: 'Ethiopia',                          currency: 'ETB', timezone: 'Africa/Addis_Ababa' },
  { code: 'FI', name: 'Finland',                           currency: 'EUR', timezone: 'Europe/Helsinki' },
  { code: 'FR', name: 'France',                            currency: 'EUR', timezone: 'Europe/Paris' },
  { code: 'GE', name: 'Georgia',                           currency: 'GEL', timezone: 'Asia/Tbilisi' },
  { code: 'DE', name: 'Germany',                           currency: 'EUR', timezone: 'Europe/Berlin' },
  { code: 'GH', name: 'Ghana',                             currency: 'GHS', timezone: 'Africa/Accra' },
  { code: 'GR', name: 'Greece',                            currency: 'EUR', timezone: 'Europe/Athens' },
  { code: 'GT', name: 'Guatemala',                         currency: 'GTQ', timezone: 'America/Guatemala' },
  { code: 'HN', name: 'Honduras',                          currency: 'HNL', timezone: 'America/Tegucigalpa' },
  { code: 'HK', name: 'Hong Kong',                         currency: 'HKD', timezone: 'Asia/Hong_Kong' },
  { code: 'HU', name: 'Hungary',                           currency: 'HUF', timezone: 'Europe/Budapest' },
  { code: 'IS', name: 'Iceland',                           currency: 'ISK', timezone: 'Atlantic/Reykjavik' },
  { code: 'IN', name: 'India',                             currency: 'INR', timezone: 'Asia/Kolkata' },
  { code: 'ID', name: 'Indonesia',                         currency: 'IDR', timezone: 'Asia/Jakarta' },
  { code: 'IQ', name: 'Iraq',                              currency: 'IQD', timezone: 'Asia/Baghdad' },
  { code: 'IE', name: 'Ireland',                           currency: 'EUR', timezone: 'Europe/Dublin' },
  { code: 'IL', name: 'Israel',                            currency: 'ILS', timezone: 'Asia/Jerusalem' },
  { code: 'IT', name: 'Italy',                             currency: 'EUR', timezone: 'Europe/Rome' },
  { code: 'CI', name: 'Ivory Coast',                       currency: 'XOF', timezone: 'Africa/Abidjan' },
  { code: 'JM', name: 'Jamaica',                           currency: 'JMD', timezone: 'America/Jamaica' },
  { code: 'JP', name: 'Japan',                             currency: 'JPY', timezone: 'Asia/Tokyo' },
  { code: 'JO', name: 'Jordan',                            currency: 'JOD', timezone: 'Asia/Amman' },
  { code: 'KZ', name: 'Kazakhstan',                        currency: 'KZT', timezone: 'Asia/Almaty' },
  { code: 'KE', name: 'Kenya',                             currency: 'KES', timezone: 'Africa/Nairobi' },
  { code: 'KW', name: 'Kuwait',                            currency: 'KWD', timezone: 'Asia/Kuwait' },
  { code: 'KG', name: 'Kyrgyzstan',                        currency: 'KGS', timezone: 'Asia/Bishkek' },
  { code: 'LA', name: 'Laos',                              currency: 'LAK', timezone: 'Asia/Vientiane' },
  { code: 'LV', name: 'Latvia',                            currency: 'EUR', timezone: 'Europe/Riga' },
  { code: 'LB', name: 'Lebanon',                           currency: 'LBP', timezone: 'Asia/Beirut' },
  { code: 'LY', name: 'Libya',                             currency: 'LYD', timezone: 'Africa/Tripoli' },
  { code: 'LT', name: 'Lithuania',                         currency: 'EUR', timezone: 'Europe/Vilnius' },
  { code: 'LU', name: 'Luxembourg',                        currency: 'EUR', timezone: 'Europe/Luxembourg' },
  { code: 'MO', name: 'Macau',                             currency: 'MOP', timezone: 'Asia/Macau' },
  { code: 'MG', name: 'Madagascar',                        currency: 'MGA', timezone: 'Indian/Antananarivo' },
  { code: 'MY', name: 'Malaysia',                          currency: 'MYR', timezone: 'Asia/Kuala_Lumpur' },
  { code: 'MV', name: 'Maldives',                          currency: 'MVR', timezone: 'Indian/Maldives' },
  { code: 'MT', name: 'Malta',                             currency: 'EUR', timezone: 'Europe/Malta' },
  { code: 'MU', name: 'Mauritius',                         currency: 'MUR', timezone: 'Indian/Mauritius' },
  { code: 'MX', name: 'Mexico',                            currency: 'MXN', timezone: 'America/Mexico_City' },
  { code: 'MD', name: 'Moldova',                           currency: 'MDL', timezone: 'Europe/Chisinau' },
  { code: 'MA', name: 'Morocco',                           currency: 'MAD', timezone: 'Africa/Casablanca' },
  { code: 'MM', name: 'Myanmar',                           currency: 'MMK', timezone: 'Asia/Yangon' },
  { code: 'NA', name: 'Namibia',                           currency: 'NAD', timezone: 'Africa/Windhoek' },
  { code: 'NP', name: 'Nepal',                             currency: 'NPR', timezone: 'Asia/Kathmandu' },
  { code: 'NL', name: 'Netherlands',                       currency: 'EUR', timezone: 'Europe/Amsterdam' },
  { code: 'NZ', name: 'New Zealand',                       currency: 'NZD', timezone: 'Pacific/Auckland' },
  { code: 'NI', name: 'Nicaragua',                         currency: 'NIO', timezone: 'America/Managua' },
  { code: 'NG', name: 'Nigeria',                           currency: 'NGN', timezone: 'Africa/Lagos' },
  { code: 'NO', name: 'Norway',                            currency: 'NOK', timezone: 'Europe/Oslo' },
  { code: 'OM', name: 'Oman',                              currency: 'OMR', timezone: 'Asia/Muscat' },
  { code: 'PK', name: 'Pakistan',                          currency: 'PKR', timezone: 'Asia/Karachi' },
  { code: 'PS', name: 'Palestine',                         currency: 'ILS', timezone: 'Asia/Gaza' },
  { code: 'PA', name: 'Panama',                            currency: 'PAB', timezone: 'America/Panama' },
  { code: 'PY', name: 'Paraguay',                          currency: 'PYG', timezone: 'America/Asuncion' },
  { code: 'PE', name: 'Peru',                              currency: 'PEN', timezone: 'America/Lima' },
  { code: 'PH', name: 'Philippines',                       currency: 'PHP', timezone: 'Asia/Manila' },
  { code: 'PL', name: 'Poland',                            currency: 'PLN', timezone: 'Europe/Warsaw' },
  { code: 'PT', name: 'Portugal',                          currency: 'EUR', timezone: 'Europe/Lisbon' },
  { code: 'QA', name: 'Qatar',                             currency: 'QAR', timezone: 'Asia/Qatar' },
  { code: 'RO', name: 'Romania',                           currency: 'RON', timezone: 'Europe/Bucharest' },
  { code: 'RU', name: 'Russia',                            currency: 'RUB', timezone: 'Europe/Moscow' },
  { code: 'RW', name: 'Rwanda',                            currency: 'RWF', timezone: 'Africa/Kigali' },
  { code: 'SA', name: 'Saudi Arabia',                      currency: 'SAR', timezone: 'Asia/Riyadh' },
  { code: 'SN', name: 'Senegal',                           currency: 'XOF', timezone: 'Africa/Dakar' },
  { code: 'RS', name: 'Serbia',                            currency: 'RSD', timezone: 'Europe/Belgrade' },
  { code: 'SG', name: 'Singapore',                         currency: 'SGD', timezone: 'Asia/Singapore' },
  { code: 'SK', name: 'Slovakia',                          currency: 'EUR', timezone: 'Europe/Bratislava' },
  { code: 'SI', name: 'Slovenia',                          currency: 'EUR', timezone: 'Europe/Ljubljana' },
  { code: 'SO', name: 'Somalia',                           currency: 'SOS', timezone: 'Africa/Mogadishu' },
  { code: 'ZA', name: 'South Africa',                      currency: 'ZAR', timezone: 'Africa/Johannesburg' },
  { code: 'KR', name: 'South Korea',                       currency: 'KRW', timezone: 'Asia/Seoul' },
  { code: 'ES', name: 'Spain',                             currency: 'EUR', timezone: 'Europe/Madrid' },
  { code: 'LK', name: 'Sri Lanka',                         currency: 'LKR', timezone: 'Asia/Colombo' },
  { code: 'SD', name: 'Sudan',                             currency: 'SDG', timezone: 'Africa/Khartoum' },
  { code: 'SE', name: 'Sweden',                            currency: 'SEK', timezone: 'Europe/Stockholm' },
  { code: 'CH', name: 'Switzerland',                       currency: 'CHF', timezone: 'Europe/Zurich' },
  { code: 'TW', name: 'Taiwan',                            currency: 'TWD', timezone: 'Asia/Taipei' },
  { code: 'TJ', name: 'Tajikistan',                        currency: 'TJS', timezone: 'Asia/Dushanbe' },
  { code: 'TZ', name: 'Tanzania',                          currency: 'TZS', timezone: 'Africa/Dar_es_Salaam' },
  { code: 'TH', name: 'Thailand',                          currency: 'THB', timezone: 'Asia/Bangkok' },
  { code: 'TN', name: 'Tunisia',                           currency: 'TND', timezone: 'Africa/Tunis' },
  { code: 'TR', name: 'Turkey',                            currency: 'TRY', timezone: 'Europe/Istanbul' },
  { code: 'TM', name: 'Turkmenistan',                      currency: 'TMT', timezone: 'Asia/Ashgabat' },
  { code: 'UG', name: 'Uganda',                            currency: 'UGX', timezone: 'Africa/Kampala' },
  { code: 'UA', name: 'Ukraine',                           currency: 'UAH', timezone: 'Europe/Kyiv' },
  { code: 'AE', name: 'United Arab Emirates',              currency: 'AED', timezone: 'Asia/Dubai' },
  { code: 'GB', name: 'United Kingdom',                    currency: 'GBP', timezone: 'Europe/London' },
  { code: 'US', name: 'United States',                     currency: 'USD', timezone: 'America/New_York' },
  { code: 'UY', name: 'Uruguay',                           currency: 'UYU', timezone: 'America/Montevideo' },
  { code: 'UZ', name: 'Uzbekistan',                        currency: 'UZS', timezone: 'Asia/Tashkent' },
  { code: 'VE', name: 'Venezuela',                         currency: 'VES', timezone: 'America/Caracas' },
  { code: 'VN', name: 'Vietnam',                           currency: 'VND', timezone: 'Asia/Ho_Chi_Minh' },
  { code: 'YE', name: 'Yemen',                             currency: 'YER', timezone: 'Asia/Aden' },
  { code: 'ZM', name: 'Zambia',                            currency: 'ZMW', timezone: 'Africa/Lusaka' },
  { code: 'ZW', name: 'Zimbabwe',                          currency: 'ZWL', timezone: 'Africa/Harare' },
];

// ENG-105: currency and time zone are picked from lists, never typed.
const CURRENCY_OPTIONS = [...new Set(COUNTRIES.map((c) => c.currency))].sort();
function zoneOptions(current) {
  let zones = [];
  try {
    zones = Intl.supportedValuesOf('timeZone');
  } catch {
    zones = COUNTRIES.map((c) => c.timezone);
  }
  return [...new Set([...zones, current].filter(Boolean))].sort();
}

// Countries with an ERP country pack; any other country is set up with a
// warning (decision D3). Same list as the server's locale_codes.
const PACK_COUNTRIES = new Set(['AE', 'BH', 'IN', 'KW', 'OM', 'QA', 'SA']);

// ENG-111: the tax registration number where VAT applies (the server
// checks the same formats). Kuwait has no VAT, so no field there.
const TAX_RULES = {
  AE: { label: 'TRN', pattern: /^\d{15}$/, hint: '15 digits' },
  BH: { label: 'VAT account number', pattern: /^\d{15}$/, hint: '15 digits' },
  IN: { label: 'GSTIN', pattern: /^\d{2}[A-Z]{5}\d{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/, hint: '15 characters, like 27AAPFU0939F1ZV' },
  OM: { label: 'VATIN', pattern: /^OM\d{10}$/, hint: 'OM followed by 10 digits' },
  QA: { label: 'TIN', pattern: /^\d{10}$/, hint: '10 digits' },
  SA: { label: 'VAT number', pattern: /^3\d{13}3$/, hint: '15 digits, starting and ending with 3' },
};

export default function OnboardingPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [serverErr, setServerErr] = useState('');
  // AUTH-703: once the wizard's Finish call succeeds, the final screen the
  // client sees is a summary of the workspace that was actually provisioned
  // (name, link, industry, modules, plan) rather than an immediate silent
  // redirect. Holds the provisioning summary from the response plus the
  // fresh token pair needed to hand off into the ERP when they continue.
  const [ready, setReady] = useState(null);
  // The F002 mfaModalOpen / mfaSnoozed state that used to sit here is gone
  // with the Security step itself (ENG-504, AUTH-504): there is no 2FA CTA to
  // open a modal from and no snooze to remember, and the banner further down
  // now says so in as many words. Nothing else in this file referenced either.
  // Onboarding is BUNDLE-driven: the tenant lands on the package for their
  // industry (resolved by name) and can toggle optional add-on packs. The
  // catalog is sourced LIVE from Grid's industry_bundle (single source of
  // truth) so seeding/editing a bundle in Grid flows straight into here.
  // ``status`` is the fetch's state (AUTH-503): the Plan step renders a
  // loading, an error or the plan, and Continue waits for 'ready'.
  const [catalogue, setCatalogue] = useState({ status: 'loading', industries: [], addons: [] });
  // "Accounting set-up" choices (GET /auth/onboarding/bundles -> accounting).
  // Left alone they send nothing, and the workspace is set up exactly as
  // before: full chart, the package's industry, a January fiscal year.
  const [acctOptions, setAcctOptions] = useState(null);
  const [acctOpen, setAcctOpen] = useState(false);
  const [acct, setAcct] = useState({
    chartSize: 'full',
    chartIndustry: null, // null = the package's default industry
    fiscalYearStartMonth: 1,
    openingBalanceDate: '',
  });
  const [state, setState] = useState({
    // Prefilled with the company typed at sign-up.
    company: storage.getSignupCompany(),
    industry: '',
    // Slug of the chosen industry bundle / package (bundle-driven select).
    bundle: '',
    companySize: '',
    // ENG-105: no country is assumed; picking one fills its currency and zone.
    country: '',
    currency: '',
    timezone: '',
    localeException: false,
    taxId: '',
    selectedAddons: [],
    // ENG-554: interface language of the person and document language of the
    // tenant. Empty = not stated; nothing is sent and nothing is defaulted.
    language: '',
    documentLanguage: '',
  });

  // Loads the catalogue; Retry calls it again. A failure is an error state,
  // never an empty catalogue: provisioning a tenant from an empty bundle
  // list gave it no modules and told nobody (AUTH-503).
  const mounted = useRef(true);
  useEffect(() => () => { mounted.current = false; }, []);
  const loadBundles = useCallback(() => {
    setCatalogue((c) => ({ ...c, status: 'loading' }));
    return api.getOnboardingBundles()
      .then((data) => {
        if (!mounted.current) return;
        setCatalogue({
          status: 'ready',
          industries: data?.industries || [],
          addons: data?.addons || [],
        });
        if (data?.accounting) setAcctOptions(data.accounting);
      })
      .catch(() => {
        if (!mounted.current) return;
        setCatalogue({ status: 'error', industries: [], addons: [] });
      });
  }, []);
  useEffect(() => { loadBundles(); }, [loadBundles]);

  // Bounce people without a valid session ,  they should go through login first.
  useEffect(() => {
    if (!storage.getToken()) navigate('/login');
  }, [navigate]);

  const country = useMemo(() => COUNTRIES.find((c) => c.code === state.country), [state.country]);
  // Country names and their order follow the language the person chose.
  const countryList = useMemo(() => localisedCountries(COUNTRIES, state.language || 'en'), [state.language]);
  // A currency or zone the country does not use needs the customer's tick.
  const localeMismatch = !!country
    && (state.currency !== country.currency || state.timezone !== country.timezone);
  const taxRule = TAX_RULES[state.country];
  const taxNumber = state.taxId.replace(/[\s-]/g, '').toUpperCase();
  const taxProblem = taxRule && taxNumber && !taxRule.pattern.test(taxNumber)
    ? `The ${taxRule.label} must be ${taxRule.hint}`
    : undefined;

  const setField = (k, v) => setState((s) => ({ ...s, [k]: v }));
  const toggleAddon = (slug) => setState((s) => ({
    ...s,
    selectedAddons: s.selectedAddons.includes(slug)
      ? s.selectedAddons.filter((x) => x !== slug)
      : [...s.selectedAddons, slug],
  }));

  // Packages first (a no-code package Grid defined is chosen here, by
  // slug), then the other industries; both straight from the catalogue.
  const packageChoices = useMemo(
    () => catalogue.industries.filter((b) => b.isPackage),
    [catalogue],
  );
  const industryChoices = useMemo(
    () => catalogue.industries.filter((b) => !b.isPackage && b.slug !== 'general'),
    [catalogue],
  );
  const bundleLabel = (b) => (b?.package?.name || b?.name || '');
  const chooseBundle = (slug) => {
    if (slug === OTHER) {
      setState((s) => ({ ...s, bundle: OTHER, industry: 'Other' }));
      return;
    }
    const b = catalogue.industries.find((x) => x.slug === slug);
    setState((s) => ({ ...s, bundle: slug, industry: b ? (b.industry || b.name) : '' }));
  };

  // The chosen bundle: by slug when the catalogue drove the choice, else
  // (fallback list) matched by name; "Other"/unmatched falls back to the
  // "General Business" package.
  // Joined on the slug only. "Other" is the catalogue's own General
  // Business bundle, chosen on purpose; nothing else falls through to it.
  const chosenSlug = state.bundle === OTHER ? 'general' : state.bundle;
  const industryBundle = useMemo(
    () => (chosenSlug && catalogue.industries.find((b) => b.slug === chosenSlug)) || null,
    [catalogue, chosenSlug],
  );

  // "Other" was chosen, so the general package is used; the Plan step says
  // so rather than presenting it as the industry's own.
  const usesGeneralFallback = state.bundle === OTHER && industryBundle?.slug === 'general';

  // The effective module set = industry bundle ∪ selected add-ons (deduped).
  const includedModules = useMemo(() => {
    const map = new Map();
    (industryBundle?.modules || []).forEach((m) => map.set(m.slug, m.name));
    state.selectedAddons.forEach((slug) => {
      const a = catalogue.addons.find((x) => x.slug === slug);
      (a?.modules || []).forEach((m) => map.set(m.slug, m.name));
    });
    return [...map.entries()].map(([slug, name]) => ({ slug, name }));
  }, [industryBundle, state.selectedAddons, catalogue]);

  // A disabled Continue with nothing else on screen reads as a broken
  // button: the company name does NOT carry over from sign-up, so step 1
  // opens with an empty required field and no indication of what is
  // missing. Name the missing fields instead of leaving the client to
  // guess (found while walking the real sign-up on staging).
  const missing = (() => {
    if (step === 1) {
      return [
        !state.company.trim() && 'company name',
        !state.bundle && 'industry',
        !state.companySize && 'company size',
      ].filter(Boolean);
    }
    if (step === 2) {
      return [
        !state.country && 'country',
        !state.currency && 'currency',
        !state.timezone && 'timezone',
        localeMismatch && !state.localeException && 'confirmation of the currency or time zone',
        taxProblem && 'a valid tax number',
      ].filter(Boolean);
    }
    return [];
  })();
  const missingHint = missing.length
    ? `Still needed: ${missing.join(', ')}`
    : '';

  const canAdvance = (() => {
    if (step === 1) {
      return state.company.trim() && state.bundle && catalogue.status === 'ready' && state.companySize;
    }
    if (step === 2) {
      return state.country && state.currency && state.timezone
        && (!localeMismatch || state.localeException) && !taxProblem;
    }
    // A resolved bundle carrying at least one module, not just a chosen
    // industry: an unloaded catalogue used to submit an empty bundle list.
    if (step === 3) return catalogue.status === 'ready' && includedModules.length > 0;
    return true;
  })();

  const finish = async () => {
    setServerErr('');
    setSubmitting(true);
    try {
      const res = await api.completeOnboarding({
        companyName: state.company.trim(),
        // The join key to the catalogue is the bundle slug (AUTH-659); the
        // server derives the industry's label and chart code from it.
        industrySlug: chosenSlug,
        companySize: state.companySize,
        country: state.country,
        currency: state.currency,
        timezone: state.timezone,
        localeException: localeMismatch && state.localeException,
        ...(state.language ? { language: state.language } : {}),
        ...(state.documentLanguage ? { documentLanguage: state.documentLanguage } : {}),
        ...(taxNumber ? { taxId: taxNumber } : {}),
        // Bundle-driven: send the industry package + chosen add-ons. The
        // backend expands these to the real module set and assigns the
        // bundles to the tenant (so it shows under its industry in Grid).
        selectedBundles: [industryBundle?.slug, ...state.selectedAddons].filter(Boolean),
        selectedModules: [],
        // Accounting set-up: only what the person changed.
        ...(acct.chartSize !== 'full' ? { chartSize: acct.chartSize } : {}),
        ...(acct.chartIndustry !== null ? { chartIndustry: acct.chartIndustry } : {}),
        ...(Number(acct.fiscalYearStartMonth) !== 1 ? { fiscalYearStartMonth: Number(acct.fiscalYearStartMonth) } : {}),
        ...(acct.openingBalanceDate ? { openingBalanceDate: acct.openingBalanceDate } : {}),
      }, storage.getToken());
      storage.saveAuth({ token: res.token, refreshToken: res.refreshToken, user: res.user });
      storage.clearRouting();
      storage.clearSignupCompany();
      // After onboarding, the backend recomputes redirectTo (the user is
      // now onboarded → ERP unless they had a pending nextUrl).
      // Fall back to the ERP host configured at build time (VITE_ERP_URL
      // in .env.production = https://dashboard.vivid360.io). Hardcoding
      // localhost here would land prod users on dev after a successful
      // onboarding submit.
      const erpFallback = import.meta.env.VITE_ERP_URL || 'http://localhost:5173/';
      // AUTH-703: show the client what was provisioned before handing off,
      // instead of redirecting straight through. ``res.provisioning`` is
      // absent on an old server mid-rollout; the summary screen still
      // renders (just without the workspace detail), so Continue is never
      // a dead end.
      setReady({
        workspace: res.provisioning || null,
        token: res.token,
        refreshToken: res.refreshToken,
        redirectTo: res.redirectTo || erpFallback,
      });
    } catch (err) {
      setServerErr(errorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  // AUTH-703: the wizard is done and the workspace exists — show what was
  // actually provisioned instead of bouncing the client straight to the ERP.
  if (ready) {
    return (
      <WorkspaceReadyScreen
        state={state}
        workspace={ready.workspace}
        onContinue={() => handoffRedirect({
          token: ready.token,
          refreshToken: ready.refreshToken,
          redirectTo: ready.redirectTo,
          navigate,
        })}
      />
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <header className="h-16 px-6 lg:px-12 flex items-center justify-between border-b border-stone-200">
        <VividWordmark />
        {/* F005 (audit finding): replace the lone "Log out" button with
            an avatar menu so future personal actions (Account, theme,
            workspace switch) do not require another header reshuffle.
            Keeps Sign out as the bottom item ,  most users still come
            here just to logout. */}
        <AvatarMenu onLogout={() => navigate('/logout')} />
      </header>

      {/* Step indicator */}
      <div className="px-6 lg:px-12 py-6 border-b border-stone-100">
        <div className="max-w-2xl mx-auto flex items-center gap-3">
          {STEPS.map((s, i) => (
            <div key={s.id} className="flex items-center gap-3 flex-1">
              <div
                className={`w-7 h-7 rounded-full flex items-center justify-center text-[12px] font-semibold transition-colors ${
                  step >= s.id ? 'bg-ink text-white' : 'bg-stone-100 text-stone-500'
                }`}
              >
                {step > s.id ? <I.Check size={12} stroke={3} /> : s.id}
              </div>
              <span className={`text-[13px] font-medium ${step === s.id ? 'text-ink' : 'text-stone-500'}`}>
                {s.label}
              </span>
              {i < STEPS.length - 1 && <div className="flex-1 h-px bg-stone-200" />}
            </div>
          ))}
        </div>
      </div>

      <div className="flex-1 px-6 lg:px-12 py-10">
        <div className="max-w-xl mx-auto">
          {step === 1 && (
            <>
              <h1 className="text-[28px] font-bold tracking-tight text-ink">Tell us about your company</h1>
              <p className="mt-2 text-[15px] text-stone-500">We will use this to set up your workspace.</p>
              <div className="mt-8 space-y-4">
                <TextField label="Company name" value={state.company}
                  onChange={(v) => setField('company', v)} placeholder="Acme Co" autoComplete="organisation" />
                {catalogue.status === 'loading' && (
                  <div role="status" className="flex items-center gap-2 text-[13px] text-stone-500">
                    <Spinner size={16} label="Loading your packages" /> Loading your industries...
                  </div>
                )}
                {catalogue.status === 'error' && (
                  <Banner variant="danger" icon={<I.AlertCircle size={14} />}>
                    <span className="flex flex-wrap items-center justify-between gap-3">
                      <span>
                        We could not load the list of industries, so we cannot set up your workspace yet.
                        Try again, or contact support@vivid360.io if it keeps happening.
                      </span>
                      <Button variant="secondary" size="sm" onClick={loadBundles}>Retry</Button>
                    </span>
                  </Banner>
                )}
                {catalogue.status === 'ready' && catalogue.industries.length === 0 && (
                  <Banner variant="danger" icon={<I.AlertCircle size={14} />}>
                    There are no industries to choose from yet, so we cannot set up your workspace.
                    Contact support@vivid360.io.
                  </Banner>
                )}
                {catalogue.industries.length > 0 && (
                  <label className="block">
                    <span className="block mb-1.5 text-[12px] font-medium text-stone-700">Industry or package</span>
                    <select
                      value={state.bundle || ''}
                      onChange={(e) => chooseBundle(e.target.value)}
                      className="field-input"
                    >
                      <option value="" disabled>Select industry or package</option>
                      {packageChoices.length > 0 && (
                        <optgroup label="Packages">
                          {packageChoices.map((b) => (
                            <option key={b.slug} value={b.slug}>{bundleLabel(b)} (package)</option>
                          ))}
                        </optgroup>
                      )}
                      {industryChoices.length > 0 && (
                        <optgroup label="Industries">
                          {industryChoices.map((b) => (
                            <option key={b.slug} value={b.slug}>{bundleLabel(b)}</option>
                          ))}
                        </optgroup>
                      )}
                      {catalogue.industries.some((b) => b.slug === 'general') && <option value={OTHER}>Other</option>}
                    </select>
                    {industryBundle?.isPackage && industryBundle?.package?.tagline && (
                      <span className="block mt-1 text-[12px] text-stone-500">{industryBundle.package.tagline}</span>
                    )}
                  </label>
                )}
                <Select label="Company size" value={state.companySize}
                  onChange={(v) => setField('companySize', v)} options={SIZES} placeholder="Number of employees" />
              </div>
            </>
          )}

          {step === 2 && (
            <>
              <h1 className="text-[28px] font-bold tracking-tight text-ink">Where do you operate?</h1>
              <p className="mt-2 text-[15px] text-stone-500">
                We will preload the right country pack ,  chart of accounts, tax rules, payslip format.
              </p>
              <div className="mt-8 space-y-4">
                <Select label="Interface language" value={state.language}
                  onChange={(v) => setField('language', v)}
                  options={LANGUAGES} optionLabel={(v) => LANGUAGE_NAMES[v]} placeholder="Choose a language" />
                <Select label="Language of your documents" value={state.documentLanguage}
                  onChange={(v) => setField('documentLanguage', v)}
                  options={DOCUMENT_LANGUAGES} optionLabel={(v) => LANGUAGE_NAMES[v]} placeholder="Choose a language" />
                <Select label="Country" value={state.country}
                  onChange={(v) => {
                    const c = COUNTRIES.find((x) => x.code === v);
                    setState((s) => ({ ...s, country: v, currency: c?.currency || s.currency, timezone: c?.timezone || s.timezone, localeException: false, taxId: '' }));
                  }}
                  placeholder="Choose a country"
                  options={countryList.map((c) => c.code)}
                  optionLabel={(code) => countryList.find((c) => c.code === code)?.name || code}
                />
                <Select label="Currency" value={state.currency}
                  onChange={(v) => setField('currency', v)}
                  placeholder="Choose a currency"
                  options={CURRENCY_OPTIONS} />
                <Select label="Timezone" value={state.timezone}
                  onChange={(v) => setField('timezone', v)}
                  placeholder="Choose a timezone"
                  options={zoneOptions(state.timezone)} />
                {country && (
                  <p className="text-[12px] text-stone-500 leading-relaxed">
                    Defaults from <span className="text-ink">{country.name}</span>: {country.currency},{' '}
                    {country.timezone}. Currency and country are fixed once you record your first
                    transaction.
                  </p>
                )}
                {localeMismatch && (
                  <label className="flex items-start gap-2 text-[12px] text-stone-700" data-testid="locale-exception">
                    <input type="checkbox" className="mt-0.5" checked={state.localeException}
                      onChange={(e) => setField('localeException', e.target.checked)} />
                    <span>
                      {country.name} normally uses {country.currency} and {country.timezone}. Tick to keep{' '}
                      {state.currency} and {state.timezone} anyway.
                    </span>
                  </label>
                )}
                {country && !PACK_COUNTRIES.has(country.code) && (
                  <p className="rounded-md bg-amber-50 px-3 py-2 text-[12px] text-amber-800" data-testid="no-pack-warning">
                    {country.name} has no country pack yet: we set up your company without a ready chart of
                    accounts, taxes or tax return, and our team will help you finish the accounting setup.
                  </p>
                )}
                {taxRule && (
                  <TextField label={`${taxRule.label} (optional)`} value={state.taxId}
                    placeholder={taxRule.hint}
                    error={taxProblem}
                    onChange={(v) => setField('taxId', v)} />
                )}
              </div>
            </>
          )}

          {step === 3 && (
            <>
              <h1 className="text-[28px] font-bold tracking-tight text-ink">Choose your plan</h1>
              {catalogue.status === 'loading' && (
                <div className="mt-8 space-y-3" aria-busy="true">
                  <div role="status" className="flex items-center gap-2 text-[13px] text-stone-500">
                    <Spinner size={16} label="Loading your packages" /> Loading your packages...
                  </div>
                  <div className="h-24 rounded-lg bg-stone-100 animate-pulse" aria-hidden="true" />
                  <div className="grid grid-cols-2 gap-2.5" aria-hidden="true">
                    <div className="h-14 rounded-lg bg-stone-100 animate-pulse" />
                    <div className="h-14 rounded-lg bg-stone-100 animate-pulse" />
                  </div>
                </div>
              )}
              {catalogue.status === 'error' && (
                <Banner variant="danger" className="mt-8" icon={<I.AlertCircle size={14} />}>
                  <span className="flex flex-wrap items-center justify-between gap-3">
                    <span>
                      We could not load your packages, so we cannot set up your workspace yet.
                      Try again, or contact support@vivid360.io if it keeps happening.
                    </span>
                    <Button variant="secondary" size="sm" onClick={loadBundles}>Retry</Button>
                  </span>
                </Banner>
              )}
              {catalogue.status === 'ready' && includedModules.length === 0 && (
                <Banner variant="danger" className="mt-8" icon={<I.AlertCircle size={14} />}>
                  The {bundleLabel(industryBundle) || state.industry || 'chosen'} package has no modules
                  yet, so there is nothing to set up. Choose another industry or package, or contact
                  support@vivid360.io.
                </Banner>
              )}
              {catalogue.status === 'ready' && includedModules.length > 0 && (
              <>
              <p className="mt-2 text-[15px] text-stone-500">
                Your <span className="text-ink font-medium">{bundleLabel(industryBundle) || state.industry}</span> package
                is ready to go. Add optional packs below. You can change modules anytime later.
              </p>
              {usesGeneralFallback && (
                <Banner variant="info" className="mt-4">
                  We have no package for {state.industry && state.industry !== 'Other' ? state.industry : 'your industry'} yet,
                  so your workspace starts with {bundleLabel(industryBundle)}.
                </Banner>
              )}
              <div className="mt-8 space-y-6 max-h-[52vh] overflow-y-auto pr-1">
                {/* Industry vertical package — included by default */}
                {industryBundle && (
                  <div className="rounded-lg border border-ink bg-stone-50 p-4">
                    <div className="flex items-center justify-between">
                      <span className="text-[14px] font-semibold text-ink">{bundleLabel(industryBundle)}</span>
                      <span className="text-[10px] font-semibold uppercase tracking-[0.08em] text-stone-500">
                        {industryBundle.isPackage ? 'Your package' : 'Your industry plan'}
                      </span>
                    </div>
                    <div className="mt-3 flex flex-wrap gap-1.5">
                      {industryBundle.modules.map((m) => (
                        <span key={m.slug}
                          className="text-[11px] px-2 py-0.5 rounded bg-white border border-stone-200 text-stone-600">
                          {m.name}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Optional add-on packages */}
                {catalogue.addons.length > 0 && (
                  <div>
                    <div className="mb-2 text-[11px] font-semibold uppercase tracking-[0.08em] text-stone-400">
                      Optional add-ons
                    </div>
                    <div className="grid grid-cols-2 gap-2.5">
                      {catalogue.addons.map((a) => {
                        const on = state.selectedAddons.includes(a.slug);
                        return (
                          <button key={a.slug}
                            onClick={() => toggleAddon(a.slug)}
                            className={`text-start p-3 rounded-lg border transition-colors cursor-pointer ${
                              on ? 'border-ink bg-stone-50' : 'border-stone-200 hover:border-stone-300'
                            }`}
                          >
                            <div className="flex items-center justify-between gap-2">
                              <span className="text-[13px] font-medium text-ink">{a.name}</span>
                              {on && <I.Check size={14} className="text-ink shrink-0" />}
                            </div>
                            <span className="text-[11px] text-stone-500 line-clamp-2">
                              {a.modules.map((m) => m.name).join(', ')}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                <p className="text-[12px] text-stone-500">
                  <span className="text-ink font-medium">{includedModules.length}</span> modules will be enabled for your workspace.
                </p>

                {acctOptions && (
                  <div className="rounded-lg border border-stone-200">
                    <button type="button" onClick={() => setAcctOpen((o) => !o)} aria-expanded={acctOpen}
                      className="w-full flex items-center justify-between px-4 py-3 text-left">
                      <span>
                        <span className="block text-[13px] font-semibold text-ink">Accounting set-up</span>
                        <span className="block text-[11px] text-stone-500">
                          {(acctOptions.chartSizes || []).find((c) => c.code === acct.chartSize)?.label || 'Full'} chart
                          {' · '}fiscal year starts in {MONTHS[Number(acct.fiscalYearStartMonth) - 1]}
                          {acct.openingBalanceDate ? ` · opening balances ${acct.openingBalanceDate}` : ''}
                        </span>
                      </span>
                      <I.ChevronRight size={16} className={`text-stone-400 transition-transform ${acctOpen ? 'rotate-90' : ''}`} />
                    </button>
                    {acctOpen && (
                      <div className="px-4 pb-4 space-y-4">
                        <fieldset>
                          <legend className="block mb-1.5 text-[12px] font-medium text-stone-700">Chart of accounts</legend>
                          <div className="flex gap-2">
                            {(acctOptions.chartSizes || []).map((c) => (
                              <label key={c.code}
                                className={`flex-1 cursor-pointer rounded-md border px-3 py-2 text-[12px] ${
                                  acct.chartSize === c.code ? 'border-ink bg-stone-50' : 'border-stone-200'}`}>
                                <input type="radio" name="chartSize" value={c.code} className="sr-only"
                                  checked={acct.chartSize === c.code}
                                  onChange={() => setAcct((a) => ({ ...a, chartSize: c.code }))} />
                                <span className="block font-medium text-ink">{c.label}</span>
                                <span className="block text-stone-500">{c.accountCount} base accounts</span>
                              </label>
                            ))}
                          </div>
                        </fieldset>
                        <label className="block">
                          <span className="block mb-1.5 text-[12px] font-medium text-stone-700">Industry accounts</span>
                          <select className="field-input"
                            value={acct.chartIndustry ?? (acctOptions.defaultIndustryByBundle || {})[industryBundle?.slug] ?? 'none'}
                            onChange={(e) => setAcct((a) => ({ ...a, chartIndustry: e.target.value }))}>
                            <option value="none">None</option>
                            {(acctOptions.chartIndustries || []).map((i) => (
                              <option key={i.code} value={i.code}>{i.name}</option>
                            ))}
                          </select>
                        </label>
                        <label className="block">
                          <span className="block mb-1.5 text-[12px] font-medium text-stone-700">Fiscal year starts</span>
                          <select className="field-input" value={acct.fiscalYearStartMonth}
                            onChange={(e) => setAcct((a) => ({ ...a, fiscalYearStartMonth: Number(e.target.value) }))}>
                            {(acctOptions.fiscalYearStartMonths || []).map((m) => (
                              <option key={m} value={m}>{MONTHS[m - 1]}</option>
                            ))}
                          </select>
                        </label>
                        <TextField label="Opening balance date (optional)" type="date" value={acct.openingBalanceDate}
                          onChange={(v) => setAcct((a) => ({ ...a, openingBalanceDate: v }))}
                          helper="The date your balances are carried over from." />
                      </div>
                    )}
                  </div>
                )}
              </div>
              </>
              )}
            </>
          )}

          {step === 4 && (
            <>
              <h1 className="text-[28px] font-bold tracking-tight text-ink">All set?</h1>
              <p className="mt-2 text-[15px] text-stone-500">Review your workspace settings before we provision it.</p>
              <dl className="mt-8 divide-y divide-stone-200 border-t border-b border-stone-200">
                <Row k="Company" v={state.company} />
                <Row k="Industry" v={industryBundle?.isPackage ? bundleLabel(industryBundle) : state.industry} />
                <Row k="Size" v={state.companySize} />
                <Row k="Country" v={COUNTRIES.find((c) => c.code === state.country)?.name || state.country} />
                <Row k="Currency" v={state.currency} />
                <Row k="Timezone" v={state.timezone} />
                <Row k="Plan" v={bundleLabel(industryBundle) || state.industry} />
                {state.selectedAddons.length > 0 && (
                  <Row k="Add-ons" v={state.selectedAddons
                    .map((s) => catalogue.addons.find((a) => a.slug === s)?.name || s)
                    .join(', ')} />
                )}
                {includedModules.length > 0 ? (
                  <Row k="Modules" v={`${includedModules.length} modules`} />
                ) : (
                  <Row k="Modules" error v="No modules: go back to Plan and choose a package with modules" />
                )}
              </dl>
              <Banner variant="warning" className="mt-6">
                Two-factor authentication is not available yet. Sign-in to this workspace is
                protected by a password alone. We will contact workspace owners when a second
                factor is ready.
              </Banner>
              <FormAlert message={serverErr} className="mt-4" />
            </>
          )}

          {missingHint && (
            <div className="mt-6 text-[13px] text-stone-500" role="status">
              {missingHint}
            </div>
          )}

          <div className="mt-10 flex items-center justify-between">
            <button
              onClick={() => setStep((s) => Math.max(1, s - 1))}
              disabled={step === 1 || submitting}
              className="btn-secondary"
            >
              <I.ChevronLeft size={14} /> Back
            </button>
            {step < STEPS.length ? (
              <button
                onClick={() => setStep((s) => s + 1)}
                disabled={!canAdvance}
                className="btn-primary"
                title={missingHint || undefined}
              >
                Continue <I.ChevronRight size={14} />
              </button>
            ) : (
              <button onClick={finish} disabled={submitting || includedModules.length === 0} className="btn-primary">
                {submitting ? (
                  <>
                    <svg role="status" aria-label="Loading" aria-busy="true" className="w-3.5 h-3.5 animate-spin" viewBox="0 0 16 16" fill="none"><circle cx="8" cy="8" r="6" stroke="currentColor" strokeOpacity="0.35" strokeWidth="2" fill="none" /><path d="M 8 2 a 6 6 0 0 1 6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" fill="none" /></svg>
                    Provisioning…
                  </>
                ) : (
                  <>Finish <I.ChevronRight size={14} /></>
                )}
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="px-6 lg:px-12"><Footer /></div>

    </div>
  );
}

// AUTH-703: the workspace summary screen shown right after Finish
// succeeds. Renders the REAL provisioned state the backend echoed back
// (``workspace``, from ``AuthResponse.provisioning``) — the workspace
// name/link, industry, module count and plan — instead of a generic
// "you're all set" message. ``workspace`` is null against an old server
// mid-rollout, or when provisioning raised (``succeeded: false``); both
// cases still show a Continue button so the client is never stuck.
function WorkspaceReadyScreen({ state, workspace, onContinue }) {
  const name = workspace?.workspaceName || state.company;
  const url = workspace?.workspaceUrl;
  const partial = workspace && workspace.succeeded === false;
  return (
    <div className="min-h-screen flex flex-col bg-white">
      <header className="h-16 px-6 lg:px-12 flex items-center border-b border-stone-200">
        <VividWordmark />
      </header>
      <div className="flex-1 px-6 lg:px-12 py-10">
        <div className="max-w-xl mx-auto">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-emerald-50 text-emerald-600">
            <I.CircleCheck size={22} />
          </div>
          <h1 className="mt-5 text-[28px] font-bold tracking-tight text-ink">
            {partial ? 'Your account is ready' : 'Your workspace is ready'}
          </h1>
          <p className="mt-2 text-[15px] text-stone-500">
            {partial
              ? 'Your account was created, but finishing the workspace set-up is taking a little longer than usual. You can start now — the rest will catch up shortly, or reach out to support if modules look incomplete.'
              : `Here is what we set up for ${name || 'your company'}.`}
          </p>

          <dl className="mt-8 divide-y divide-stone-200 border-t border-b border-stone-200">
            <Row k="Workspace" v={name} />
            {url && (
              <div className="flex justify-between py-3 text-[14px]">
                <dt className="text-stone-500">Link</dt>
                <dd className="text-ink font-medium truncate max-w-[60%]" title={url}>{url}</dd>
              </div>
            )}
            {!partial && workspace?.industry && <Row k="Industry" v={workspace.industry} />}
            {!partial && (
              <Row k="Modules" v={`${(workspace?.modules || []).length} enabled`} />
            )}
            {!partial && workspace?.plan && (
              <Row k="Plan" v={
                workspace.trialDays
                  ? `${workspace.plan} (${workspace.trialDays}-day trial)`
                  : workspace.plan
              } />
            )}
          </dl>

          <div className="mt-10">
            <button onClick={onContinue} className="btn-primary">
              Go to your workspace <I.ChevronRight size={14} />
            </button>
          </div>
        </div>
      </div>
      <div className="px-6 lg:px-12"><Footer /></div>
    </div>
  );
}

// `error` marks a row whose value is a problem rather than a fact: shown in
// red and announced, because a summary row that has silently failed otherwise
// reads as a normal value (AUTH-91).
function Row({ k, v, error }) {
  return (
    <div className="flex justify-between py-3 text-[14px]">
      <dt className="text-stone-500">{k}</dt>
      <dd className={error ? 'text-red-600 font-medium' : 'text-ink font-medium'} role={error ? 'alert' : undefined}>
        {v || ', '}
      </dd>
    </div>
  );
}

// F005 (audit finding): avatar menu used in the Onboarding header in
// place of the previous lone "Log out" pill. Items mirror the ERP
// shell's UserMenu so the visual + interaction language is consistent
// across the auth surface and the app shell:
//   - Account settings  → placeholder, links to /settings (post-onboard)
//   - Switch workspace  → placeholder for the multi-tenant picker
//   - Help              → mailto with a sensible subject
//   - Theme             → no-op for now (auth UI is light-only); the
//                         hook stub keeps the menu shape stable so the
//                         ERP-side toggle can be wired without another
//                         layout change later
//   - Sign out          → existing /logout flow
function AvatarMenu({ onLogout }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const user = storage.getUser?.() || {};
  // Build initials from full name → email local part → "?"; mirrors
  // the ERP shell's displayName() helper so the auth surface and the
  // app shell render the same glyph for the same user.
  const fullName = (user.name || `${user.firstName || ''} ${user.lastName || ''}`).trim();
  const email = user.email || '';
  const labelSource = fullName || (email.includes('@') ? email.split('@')[0] : email) || '';
  const initial = (labelSource.charAt(0) || '?').toUpperCase();

  // Close on outside click + Escape.
  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`User menu for ${labelSource || 'account'}`}
        className="flex items-center gap-2 rounded-full border border-stone-200 px-2 py-1 hover:bg-stone-50"
      >
        <span
          aria-hidden="true"
          className="flex h-7 w-7 items-center justify-center rounded-full bg-ink text-white text-[12px] font-bold"
        >
          {initial}
        </span>
        <I.ChevronRight
          size={12}
          className={`transition-transform ${open ? 'rotate-90' : 'rotate-0'} text-stone-500`}
        />
      </button>

      {open && (
        <div
          role="menu"
          className="absolute end-0 end-0 top-full z-40 mt-1.5 w-56 overflow-hidden rounded-lg border border-stone-200 bg-white py-1 shadow-lg"
        >
          {labelSource && (
            <div className="border-b border-stone-100 px-3 py-2">
              <div className="truncate text-[13px] font-semibold text-ink">{labelSource}</div>
              {email && <div className="truncate text-[11px] text-stone-500">{email}</div>}
            </div>
          )}
          <a
            role="menuitem"
            href="/settings"
            onClick={(e) => {
              // Settings page lives in the ERP shell, not the auth UI;
              // the actual redirect happens after onboarding finishes.
              // For now we keep the menu item as a discoverability
              // surface and dismiss the popover.
              e.preventDefault();
              setOpen(false);
            }}
            className="flex w-full items-center gap-2.5 px-3 py-2 text-[13px] text-ink hover:bg-stone-50"
          >
            <I.Key size={14} className="text-stone-500" /> Account settings
          </a>
          <a
            role="menuitem"
            href="#switch-workspace"
            onClick={(e) => {
              // Workspace picker ships with F004 in the ERP shell. Here
              // it is a placeholder so the auth-side avatar menu keeps
              // parity with the ERP one.
              e.preventDefault();
              setOpen(false);
            }}
            className="flex w-full items-center gap-2.5 px-3 py-2 text-[13px] text-ink hover:bg-stone-50"
          >
            <I.ChevronRight size={14} className="text-stone-500" /> Switch workspace
          </a>
          <a
            role="menuitem"
            href="mailto:support@vivid360.io?subject=Onboarding%20help"
            onClick={() => setOpen(false)}
            className="flex w-full items-center gap-2.5 px-3 py-2 text-[13px] text-ink hover:bg-stone-50"
          >
            <I.Mail size={14} className="text-stone-500" /> Help
          </a>
          {/* TODO(F005): wire to the global theme store once the auth
              UI grows a dark mode. The toggle stays as a no-op so the
              menu shape stays stable. */}
          <button
            type="button"
            role="menuitem"
            onClick={() => setOpen(false)}
            className="flex w-full items-center gap-2.5 px-3 py-2 text-[13px] text-ink hover:bg-stone-50"
          >
            <I.Eye size={14} className="text-stone-500" /> Theme: System
          </button>
          <div className="my-1 border-t border-stone-200" />
          <button
            type="button"
            role="menuitem"
            onClick={() => { setOpen(false); onLogout(); }}
            className="flex w-full items-center gap-2.5 px-3 py-2 text-[13px] text-ink hover:bg-stone-50"
          >
            <I.Logout size={14} className="text-stone-500" /> Sign out
          </button>
        </div>
      )}
    </div>
  );
}

function Select({ label, value, onChange, options, placeholder, optionLabel }) {
  return (
    <label className="block">
      <span className="block mb-1.5 text-[12px] font-medium text-stone-700">{label}</span>
      <select
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        className="field-input"
      >
        {placeholder && <option value="" disabled>{placeholder}</option>}
        {options.map((o) => (
          <option key={o} value={o}>{optionLabel ? optionLabel(o) : o}</option>
        ))}
      </select>
    </label>
  );
}
